/**
 * User signup form + recovery modal, social-proof withdraw toast copy, and forgot-password
 * extras (link line, toasts, missing field labels). Merged into each non-English locale.
 * English lives in `en.js`; Spanish keeps inline `user.socialProof.*` in `es.js` (not duplicated here).
 */

function social(label, withdrew, justNow, sec30, min1, min2, min3) {
  return {
    'user.socialProof.network': 'USDT (TRC20)',
    'user.socialProof.label': label,
    'user.socialProof.withdrew': withdrew,
    'user.socialProof.time.justNow': justNow,
    'user.socialProof.time.sec30': sec30,
    'user.socialProof.time.min1': min1,
    'user.socialProof.time.min2': min2,
    'user.socialProof.time.min3': min3,
  };
}

function forgotExtras(o) {
  return {
    'auth.user.forgot.needAccountSignup': o.needAccountSignup,
    'auth.user.forgot.recoveryPhraseLong': o.recoveryPhraseLong,
    'auth.user.forgot.updating': o.updating,
    'auth.user.forgot.error.usernameRequired': o.errUser,
    'auth.user.forgot.error.emailRequired': o.errEmail,
    'auth.user.forgot.error.recoveryRequired': o.errRecovery,
    'auth.user.forgot.error.newPasswordRequired': o.errNewPw,
    'auth.user.forgot.error.newPasswordMin': o.errNewPwMin,
    'auth.user.forgot.error.passwordsMismatch': o.errMismatch,
    'auth.user.forgot.error.recoveryFailed': o.errFail,
    'auth.user.forgot.success.passwordUpdated': o.successPw,
  };
}

const signupEs = {
  'user.signup.title': 'Registro de usuario',
  'user.signup.subtitle': 'Crea tu cuenta del panel de usuario.',
  'user.signup.secondaryText': '¿Ya tienes cuenta?',
  'user.signup.secondaryLabel': 'Entrar',
  'user.signup.toastCreated': 'Cuenta creada — ya puedes iniciar sesión.',
  'user.signup.toastSaveRecoveryFirst':
    'Cuenta creada — guarda tu frase de recuperación antes de continuar',
  'user.signup.error.nameRequired': 'El nombre completo es obligatorio.',
  'user.signup.error.usernameAlphanumeric': 'El usuario solo puede contener letras y números.',
  'user.signup.error.usernameMin': 'El usuario debe tener al menos {{min}} caracteres.',
  'user.signup.error.usernameMax': 'El usuario debe tener como máximo {{max}} caracteres.',
  'user.signup.error.usernameTaken': 'Este nombre de usuario ya está en uso.',
  'user.signup.error.usernameCheckWait': 'Espera a que termine la comprobación del usuario.',
  'user.signup.error.emailRequired': 'El correo es obligatorio.',
  'user.signup.error.passwordRequired': 'La contraseña es obligatoria.',
  'user.signup.error.confirmRequired': 'Confirmar la contraseña es obligatorio.',
  'user.signup.error.passwordsMismatch': 'Las contraseñas no coinciden.',
  'user.signup.error.passwordMin': 'La contraseña debe tener al menos 8 caracteres.',
  'user.signup.error.captchaWrong': 'El captcha es obligatorio y debe ser correcto.',
  'user.signup.error.referralRequired': 'El ID de referido es obligatorio.',
  'user.signup.error.referralInvalid': 'El ID de referido solo puede contener letras y números.',
  'user.signup.error.registrationFailed': 'Error en el registro',
  'user.signup.form.labelFullName': 'Nombre completo',
  'user.signup.form.placeholderFullName': 'Tu nombre completo',
  'user.signup.form.labelUsername': 'Usuario',
  'user.signup.form.placeholderUsername': 'Crea un usuario',
  'user.signup.form.usernameHint': '{{min}}–{{max}} caracteres, solo letras y números.',
  'user.signup.form.labelEmail': 'Correo',
  'user.signup.form.placeholderEmail': 'Introduce el correo',
  'user.signup.form.labelPhone': 'Teléfono (opcional)',
  'user.signup.form.placeholderPhone': '+1… o solo dígitos',
  'user.signup.form.labelReferral': 'ID de referido (obligatorio)',
  'user.signup.form.placeholderReferral': 'Usuario del referidor',
  'user.signup.form.referralHint':
    'Introduce un usuario o agente. Los usuarios pueden referir usuarios; los agentes pueden referir usuarios.',
  'user.signup.form.labelPassword': 'Contraseña (obligatoria)',
  'user.signup.form.placeholderPassword': 'Crea una contraseña',
  'user.signup.form.labelConfirmPassword': 'Confirmar contraseña (obligatoria)',
  'user.signup.form.placeholderConfirmPassword': 'Confirma la contraseña',
  'user.signup.form.captchaRobot': 'No soy un robot (obligatorio)',
  'user.signup.form.captchaQuestion': 'Captcha: ¿cuánto es {{a}} + {{b}}?',
  'user.signup.form.placeholderCaptcha': 'Respuesta del captcha',
  'user.signup.form.submitting': 'Creando…',
  'user.signup.form.submit': 'Crear cuenta',
  'user.signup.username.available': 'Este usuario está disponible.',
  'user.signup.username.taken': 'Este usuario ya está en uso.',
  'user.signup.username.invalidChars': 'Usa solo letras y números (sin espacios).',
  'user.signup.username.invalidMin': 'El usuario debe tener al menos {{min}} caracteres.',
  'user.signup.username.invalidGeneric': 'Este usuario no se puede usar.',
  'user.signup.recovery.title': 'Guarda tu frase de recuperación',
  'user.signup.recovery.body':
    'Solo podemos mostrarla ahora. Escríbela y guárdala sin conexión. La necesitarás para restablecer la contraseña si la olvidas.',
  'user.signup.recovery.warn':
    'Nunca compartas esta frase. Quien la tenga puede restablecer tu contraseña.',
  'user.signup.recovery.copy': 'Copiar frase',
  'user.signup.recovery.copiedToast': 'Frase copiada',
  'user.signup.recovery.checkbox':
    'He anotado o guardado de forma segura mi frase de recuperación de 12 palabras.',
  'user.signup.recovery.continue': 'Continuar para iniciar sesión',
};

const signupDe = {
  'user.signup.title': 'Nutzer-Registrierung',
  'user.signup.subtitle': 'Erstellen Sie Ihr Nutzerpanel-Konto.',
  'user.signup.secondaryText': 'Bereits ein Konto?',
  'user.signup.secondaryLabel': 'Anmelden',
  'user.signup.toastCreated': 'Konto erstellt — Sie können sich jetzt anmelden.',
  'user.signup.toastSaveRecoveryFirst':
    'Konto erstellt — speichern Sie Ihre Wiederherstellungsphrase, bevor Sie fortfahren',
  'user.signup.error.nameRequired': 'Vollständiger Name ist erforderlich.',
  'user.signup.error.usernameAlphanumeric': 'Der Benutzername darf nur Buchstaben und Zahlen enthalten.',
  'user.signup.error.usernameMin': 'Der Benutzername muss mindestens {{min}} Zeichen haben.',
  'user.signup.error.usernameMax': 'Der Benutzername darf höchstens {{max}} Zeichen haben.',
  'user.signup.error.usernameTaken': 'Dieser Benutzername ist bereits vergeben.',
  'user.signup.error.usernameCheckWait': 'Bitte warten Sie, bis die Benutzername-Prüfung abgeschlossen ist.',
  'user.signup.error.emailRequired': 'E-Mail ist erforderlich.',
  'user.signup.error.passwordRequired': 'Passwort ist Pflicht.',
  'user.signup.error.confirmRequired': 'Passwortbestätigung ist Pflicht.',
  'user.signup.error.passwordsMismatch': 'Passwörter stimmen nicht überein.',
  'user.signup.error.passwordMin': 'Das Passwort muss mindestens 8 Zeichen haben.',
  'user.signup.error.captchaWrong': 'Captcha ist Pflicht und muss richtig sein.',
  'user.signup.error.referralRequired': 'Empfehlungs-ID ist erforderlich.',
  'user.signup.error.referralInvalid':
    'Die Empfehlungs-ID darf nur Buchstaben und Zahlen enthalten.',
  'user.signup.error.registrationFailed': 'Registrierung fehlgeschlagen',
  'user.signup.form.labelFullName': 'Vollständiger Name',
  'user.signup.form.placeholderFullName': 'Ihr vollständiger Name',
  'user.signup.form.labelUsername': 'Benutzername',
  'user.signup.form.placeholderUsername': 'Benutzername wählen',
  'user.signup.form.usernameHint': '{{min}}–{{max}} Zeichen, nur Buchstaben und Zahlen.',
  'user.signup.form.labelEmail': 'E-Mail',
  'user.signup.form.placeholderEmail': 'E-Mail eingeben',
  'user.signup.form.labelPhone': 'Telefonnummer (optional)',
  'user.signup.form.placeholderPhone': '+1… oder nur Ziffern',
  'user.signup.form.labelReferral': 'Empfehlungs-ID (Pflicht)',
  'user.signup.form.placeholderReferral': 'Benutzername des Empfehlenden',
  'user.signup.form.referralHint':
    'Nutzer- oder Agenten-Benutzername. Nutzer können Nutzer werben; Agenten können Nutzer werben.',
  'user.signup.form.labelPassword': 'Passwort (Pflicht)',
  'user.signup.form.placeholderPassword': 'Passwort erstellen',
  'user.signup.form.labelConfirmPassword': 'Passwort bestätigen (Pflicht)',
  'user.signup.form.placeholderConfirmPassword': 'Passwort bestätigen',
  'user.signup.form.captchaRobot': 'Ich bin kein Roboter (Pflicht)',
  'user.signup.form.captchaQuestion': 'Captcha: Wie viel ist {{a}} + {{b}}?',
  'user.signup.form.placeholderCaptcha': 'Captcha-Antwort eingeben',
  'user.signup.form.submitting': 'Wird erstellt…',
  'user.signup.form.submit': 'Konto erstellen',
  'user.signup.username.available': 'Dieser Benutzername ist verfügbar.',
  'user.signup.username.taken': 'Dieser Benutzername ist bereits vergeben.',
  'user.signup.username.invalidChars': 'Nur Buchstaben und Zahlen (keine Leerzeichen).',
  'user.signup.username.invalidMin': 'Der Benutzername muss mindestens {{min}} Zeichen haben.',
  'user.signup.username.invalidGeneric': 'Dieser Benutzername kann nicht verwendet werden.',
  'user.signup.recovery.title': 'Wiederherstellungsphrase sichern',
  'user.signup.recovery.body':
    'Wir können sie nur dieses eine Mal anzeigen. Notieren und offline aufbewahren. Sie brauchen sie, um Ihr Passwort zurückzusetzen, falls Sie es vergessen.',
  'user.signup.recovery.warn':
    'Teilen Sie diese Phrase niemals. Wer sie hat, kann Ihr Passwort zurücksetzen.',
  'user.signup.recovery.copy': 'Phrase kopieren',
  'user.signup.recovery.copiedToast': 'Phrase kopiert',
  'user.signup.recovery.checkbox':
    'Ich habe meine 12-Wort-Wiederherstellungsphrase notiert oder sicher gespeichert.',
  'user.signup.recovery.continue': 'Weiter zur Anmeldung',
};

const forgotEs = forgotExtras({
  needAccountSignup: '¿Necesitas cuenta? Regístrate',
  recoveryPhraseLong: 'Frase de recuperación de 12 palabras',
  updating: 'Actualizando…',
  errUser: 'El usuario es obligatorio.',
  errEmail: 'El correo es obligatorio.',
  errRecovery: 'La frase de recuperación es obligatoria.',
  errNewPw: 'La nueva contraseña es obligatoria.',
  errNewPwMin: 'La nueva contraseña debe tener al menos 8 caracteres.',
  errMismatch: 'La nueva contraseña y la confirmación no coinciden.',
  errFail: 'Error en la recuperación',
  successPw: 'Contraseña actualizada — ya puedes iniciar sesión',
});

const forgotDe = forgotExtras({
  needAccountSignup: 'Noch kein Konto? Registrieren',
  recoveryPhraseLong: '12-Wort-Wiederherstellungsphrase',
  updating: 'Aktualisierung…',
  errUser: 'Benutzername ist erforderlich.',
  errEmail: 'E-Mail ist erforderlich.',
  errRecovery: 'Wiederherstellungsphrase ist erforderlich.',
  errNewPw: 'Neues Passwort ist erforderlich.',
  errNewPwMin: 'Neues Passwort muss mindestens 8 Zeichen haben.',
  errMismatch: 'Neues Passwort und Bestätigung stimmen nicht überein.',
  errFail: 'Wiederherstellung fehlgeschlagen',
  successPw: 'Passwort aktualisiert — Sie können sich anmelden',
});

const socialDe = social(
  'Auszahlung',
  'Erfolgreich {{amount}} über {{network}} abgehoben',
  'gerade eben',
  'vor 30 s',
  'vor 1 Min',
  'vor 2 Min',
  'vor 3 Min'
);

const signupFr = {
  'user.signup.title': 'Inscription utilisateur',
  'user.signup.subtitle': 'Créez votre compte du panneau utilisateur.',
  'user.signup.secondaryText': 'Vous avez déjà un compte ?',
  'user.signup.secondaryLabel': 'Connexion',
  'user.signup.toastCreated': 'Compte créé — vous pouvez vous connecter.',
  'user.signup.toastSaveRecoveryFirst':
    'Compte créé — enregistrez votre phrase de récupération avant de continuer',
  'user.signup.error.nameRequired': 'Le nom complet est obligatoire.',
  'user.signup.error.usernameAlphanumeric': "Le nom d'utilisateur ne doit contenir que des lettres et des chiffres.",
  'user.signup.error.usernameMin': "Le nom d'utilisateur doit comporter au moins {{min}} caractères.",
  'user.signup.error.usernameMax': "Le nom d'utilisateur doit comporter au plus {{max}} caractères.",
  'user.signup.error.usernameTaken': "Ce nom d'utilisateur est déjà pris.",
  'user.signup.error.usernameCheckWait': "Attendez la fin de la vérification du nom d'utilisateur.",
  'user.signup.error.emailRequired': "L'e-mail est obligatoire.",
  'user.signup.error.passwordRequired': 'Le mot de passe est obligatoire.',
  'user.signup.error.confirmRequired': 'La confirmation du mot de passe est obligatoire.',
  'user.signup.error.passwordsMismatch': 'Les mots de passe ne correspondent pas.',
  'user.signup.error.passwordMin': 'Le mot de passe doit contenir au moins 8 caractères.',
  'user.signup.error.captchaWrong': 'Le captcha est obligatoire et doit être correct.',
  'user.signup.error.referralRequired': "L'ID de parrainage est obligatoire.",
  'user.signup.error.referralInvalid':
    "L'ID de parrainage ne doit contenir que des lettres et des chiffres.",
  'user.signup.error.registrationFailed': "Échec de l'inscription",
  'user.signup.form.labelFullName': 'Nom complet',
  'user.signup.form.placeholderFullName': 'Votre nom complet',
  'user.signup.form.labelUsername': "Nom d'utilisateur",
  'user.signup.form.placeholderUsername': "Créer un nom d'utilisateur",
  'user.signup.form.usernameHint': '{{min}}–{{max}} caractères, lettres et chiffres uniquement.',
  'user.signup.form.labelEmail': 'E-mail',
  'user.signup.form.placeholderEmail': "Saisir l'e-mail",
  'user.signup.form.labelPhone': 'Téléphone (optionnel)',
  'user.signup.form.placeholderPhone': '+1… ou chiffres uniquement',
  'user.signup.form.labelReferral': "ID de parrainage (obligatoire)",
  'user.signup.form.placeholderReferral': "Nom d'utilisateur du parrain",
  'user.signup.form.referralHint':
    "Saisissez un utilisateur ou un agent. Les utilisateurs parrainent des utilisateurs ; les agents peuvent parrainer des utilisateurs.",
  'user.signup.form.labelPassword': 'Mot de passe (obligatoire)',
  'user.signup.form.placeholderPassword': 'Créer un mot de passe',
  'user.signup.form.labelConfirmPassword': 'Confirmer le mot de passe (obligatoire)',
  'user.signup.form.placeholderConfirmPassword': 'Confirmer le mot de passe',
  'user.signup.form.captchaRobot': "Je ne suis pas un robot (obligatoire)",
  'user.signup.form.captchaQuestion': 'Captcha : combien font {{a}} + {{b}} ?',
  'user.signup.form.placeholderCaptcha': 'Réponse au captcha',
  'user.signup.form.submitting': 'Création…',
  'user.signup.form.submit': 'Créer un compte',
  'user.signup.username.available': "Ce nom d'utilisateur est disponible.",
  'user.signup.username.taken': "Ce nom d'utilisateur est déjà pris.",
  'user.signup.username.invalidChars': 'Utilisez uniquement des lettres et des chiffres (sans espaces).',
  'user.signup.username.invalidMin': "Le nom d'utilisateur doit comporter au moins {{min}} caractères.",
  'user.signup.username.invalidGeneric': "Ce nom d'utilisateur ne peut pas être utilisé.",
  'user.signup.recovery.title': 'Enregistrez votre phrase de récupération',
  'user.signup.recovery.body':
    "C'est la seule fois où nous pouvons l'afficher. Notez-la et conservez-la hors ligne. Vous en aurez besoin pour réinitialiser votre mot de passe.",
  'user.signup.recovery.warn':
    'Ne partagez jamais cette phrase. Quiconque la possède peut réinitialiser votre mot de passe.',
  'user.signup.recovery.copy': 'Copier la phrase',
  'user.signup.recovery.copiedToast': 'Phrase copiée',
  'user.signup.recovery.checkbox':
    "J'ai noté ou sauvegardé en lieu sûr ma phrase de récupération de 12 mots.",
  'user.signup.recovery.continue': 'Continuer vers la connexion',
};

const forgotFr = forgotExtras({
  needAccountSignup: 'Pas de compte ? Inscrivez-vous',
  recoveryPhraseLong: 'Phrase de récupération de 12 mots',
  updating: 'Mise à jour…',
  errUser: "Le nom d'utilisateur est obligatoire.",
  errEmail: "L'e-mail est obligatoire.",
  errRecovery: 'La phrase de récupération est obligatoire.',
  errNewPw: 'Le nouveau mot de passe est obligatoire.',
  errNewPwMin: 'Le nouveau mot de passe doit contenir au moins 8 caractères.',
  errMismatch: 'Le nouveau mot de passe et la confirmation ne correspondent pas.',
  errFail: 'Échec de la récupération',
  successPw: 'Mot de passe mis à jour — vous pouvez vous connecter',
});

const socialFr = social(
  'Retrait',
  'Retrait réussi de {{amount}} via {{network}}',
  "à l'instant",
  'il y a 30 s',
  'il y a 1 min',
  'il y a 2 min',
  'il y a 3 min'
);

const signupAr = {
  'user.signup.title': 'تسجيل المستخدم',
  'user.signup.subtitle': 'أنشئ حساب لوحة المستخدم.',
  'user.signup.secondaryText': 'لديك حساب بالفعل؟',
  'user.signup.secondaryLabel': 'دخول',
  'user.signup.toastCreated': 'تم إنشاء الحساب — يمكنك تسجيل الدخول الآن.',
  'user.signup.toastSaveRecoveryFirst':
    'تم إنشاء الحساب — احفظ عبارة الاسترداد قبل المتابعة',
  'user.signup.error.nameRequired': 'الاسم الكامل مطلوب.',
  'user.signup.error.usernameAlphanumeric': 'اسم المستخدم يجب أن يحتوي على أحرف وأرقام فقط.',
  'user.signup.error.usernameMin': 'اسم المستخدم يجب أن يكون {{min}} أحرف على الأقل.',
  'user.signup.error.usernameMax': 'اسم المستخدم يجب ألا يتجاوز {{max}} حرفاً.',
  'user.signup.error.usernameTaken': 'اسم المستخدم هذا مستخدم بالفعل.',
  'user.signup.error.usernameCheckWait': 'انتظر حتى تنتهي عملية التحقق من اسم المستخدم.',
  'user.signup.error.emailRequired': 'البريد الإلكتروني مطلوب.',
  'user.signup.error.passwordRequired': 'كلمة المرور إلزامية.',
  'user.signup.error.confirmRequired': 'تأكيد كلمة المرور إلزامي.',
  'user.signup.error.passwordsMismatch': 'كلمتا المرور غير متطابقتين.',
  'user.signup.error.passwordMin': 'كلمة المرور يجب أن تكون 8 أحرف على الأقل.',
  'user.signup.error.captchaWrong': 'الكابتشا إلزامية ويجب أن تكون صحيحة.',
  'user.signup.error.referralRequired': 'رمز الإحالة مطلوب.',
  'user.signup.error.referralInvalid': 'رمز الإحالة يجب أن يحتوي على أحرف وأرقام فقط.',
  'user.signup.error.registrationFailed': 'فشل التسجيل',
  'user.signup.form.labelFullName': 'الاسم الكامل',
  'user.signup.form.placeholderFullName': 'اسمك الكامل',
  'user.signup.form.labelUsername': 'اسم المستخدم',
  'user.signup.form.placeholderUsername': 'أنشئ اسم مستخدم',
  'user.signup.form.usernameHint': '{{min}}–{{max}} أحرف، أحرف وأرقام فقط.',
  'user.signup.form.labelEmail': 'البريد الإلكتروني',
  'user.signup.form.placeholderEmail': 'أدخل البريد',
  'user.signup.form.labelPhone': 'رقم الهاتف (اختياري)',
  'user.signup.form.placeholderPhone': '+1… أو أرقام فقط',
  'user.signup.form.labelReferral': 'رمز الإحالة (إلزامي)',
  'user.signup.form.placeholderReferral': 'اسم مستخدم المُحيل',
  'user.signup.form.referralHint':
    'أدخل مستخدماً أو وكيلاً. المستخدمون يحيلون مستخدمين؛ الوكلاء يمكنهم إحالة مستخدمين.',
  'user.signup.form.labelPassword': 'كلمة المرور (إلزامي)',
  'user.signup.form.placeholderPassword': 'أنشئ كلمة مرور',
  'user.signup.form.labelConfirmPassword': 'تأكيد كلمة المرور (إلزامي)',
  'user.signup.form.placeholderConfirmPassword': 'أكد كلمة المرور',
  'user.signup.form.captchaRobot': 'لست روبوتاً (إلزامي)',
  'user.signup.form.captchaQuestion': 'الكابتشا: كم {{a}} + {{b}}؟',
  'user.signup.form.placeholderCaptcha': 'أدخل إجابة الكابتشا',
  'user.signup.form.submitting': 'جاري الإنشاء…',
  'user.signup.form.submit': 'إنشاء حساب',
  'user.signup.username.available': 'اسم المستخدم متاح.',
  'user.signup.username.taken': 'اسم المستخدم مستخدم بالفعل.',
  'user.signup.username.invalidChars': 'استخدم أحرفاً وأرقاماً فقط (بدون مسافات).',
  'user.signup.username.invalidMin': 'اسم المستخدم يجب أن يكون {{min}} أحرف على الأقل.',
  'user.signup.username.invalidGeneric': 'لا يمكن استخدام اسم المستخدم هذا.',
  'user.signup.recovery.title': 'احفظ عبارة الاسترداد',
  'user.signup.recovery.body':
    'نعرضها هذه المرة فقط. اكتبها واحفظها دون اتصال. ستحتاجها لإعادة تعيين كلمة المرور إذا نسيتها.',
  'user.signup.recovery.warn':
    'لا تشارك هذه العبارة أبداً. من يملكها يمكنه إعادة تعيين كلمة مرورك.',
  'user.signup.recovery.copy': 'نسخ العبارة',
  'user.signup.recovery.copiedToast': 'تم نسخ العبارة',
  'user.signup.recovery.checkbox':
    'لقد كتبتُ أو حفظتُ بأمان عبارة الاسترداد المكونة من 12 كلمة.',
  'user.signup.recovery.continue': 'متابعة لتسجيل الدخول',
};

const forgotAr = forgotExtras({
  needAccountSignup: 'ليس لديك حساب؟ سجّل',
  recoveryPhraseLong: 'عبارة الاسترداد المكونة من 12 كلمة',
  updating: 'جاري التحديث…',
  errUser: 'اسم المستخدم مطلوب.',
  errEmail: 'البريد الإلكتروني مطلوب.',
  errRecovery: 'عبارة الاسترداد مطلوبة.',
  errNewPw: 'كلمة المرور الجديدة مطلوبة.',
  errNewPwMin: 'كلمة المرور الجديدة يجب أن تكون 8 أحرف على الأقل.',
  errMismatch: 'كلمة المرور الجديدة والتأكيد غير متطابقتين.',
  errFail: 'فشل الاسترداد',
  successPw: 'تم تحديث كلمة المرور — يمكنك تسجيل الدخول',
});

const socialAr = social(
  'سحب',
  'سحب {{amount}} بنجاح عبر {{network}}',
  'الآن',
  'منذ 30 ث',
  'منذ 1 د',
  'منذ 2 د',
  'منذ 3 د'
);

const signupJa = {
  'user.signup.title': 'ユーザー登録',
  'user.signup.subtitle': 'ユーザーパネルのアカウントを作成します。',
  'user.signup.secondaryText': 'すでにアカウントをお持ちですか？',
  'user.signup.secondaryLabel': 'ログイン',
  'user.signup.toastCreated': 'アカウントを作成しました — ログインできます。',
  'user.signup.toastSaveRecoveryFirst':
    'アカウントを作成しました — 続ける前にリカバリーフレーズを保存してください',
  'user.signup.error.nameRequired': '氏名は必須です。',
  'user.signup.error.usernameAlphanumeric': 'ユーザー名は英数字のみにしてください。',
  'user.signup.error.usernameMin': 'ユーザー名は{{min}}文字以上にしてください。',
  'user.signup.error.usernameMax': 'ユーザー名は{{max}}文字以下にしてください。',
  'user.signup.error.usernameTaken': 'このユーザー名は既に使用されています。',
  'user.signup.error.usernameCheckWait': 'ユーザー名の確認が終わるまでお待ちください。',
  'user.signup.error.emailRequired': 'メールアドレスは必須です。',
  'user.signup.error.passwordRequired': 'パスワードは必須です。',
  'user.signup.error.confirmRequired': 'パスワード確認は必須です。',
  'user.signup.error.passwordsMismatch': 'パスワードが一致しません。',
  'user.signup.error.passwordMin': 'パスワードは8文字以上にしてください。',
  'user.signup.error.captchaWrong': 'CAPTCHAは必須で、正しく入力してください。',
  'user.signup.error.referralRequired': '紹介IDは必須です。',
  'user.signup.error.referralInvalid': '紹介IDは英数字のみ使用できます。',
  'user.signup.error.registrationFailed': '登録に失敗しました',
  'user.signup.form.labelFullName': '氏名',
  'user.signup.form.placeholderFullName': 'お名前',
  'user.signup.form.labelUsername': 'ユーザー名',
  'user.signup.form.placeholderUsername': 'ユーザー名を作成',
  'user.signup.form.usernameHint': '{{min}}–{{max}}文字、英数字のみ。',
  'user.signup.form.labelEmail': 'メール',
  'user.signup.form.placeholderEmail': 'メールを入力',
  'user.signup.form.labelPhone': '電話番号（任意）',
  'user.signup.form.placeholderPhone': '+1… または数字のみ',
  'user.signup.form.labelReferral': '紹介ID（必須）',
  'user.signup.form.placeholderReferral': '紹介者のユーザー名',
  'user.signup.form.referralHint':
    'ユーザーまたはエージェントのユーザー名。ユーザーはユーザーを、エージェントはユーザーを紹介できます。',
  'user.signup.form.labelPassword': 'パスワード（必須）',
  'user.signup.form.placeholderPassword': 'パスワードを作成',
  'user.signup.form.labelConfirmPassword': 'パスワード確認（必須）',
  'user.signup.form.placeholderConfirmPassword': 'パスワードを再入力',
  'user.signup.form.captchaRobot': '私はロボットではありません（必須）',
  'user.signup.form.captchaQuestion': 'CAPTCHA: {{a}} + {{b}} は？',
  'user.signup.form.placeholderCaptcha': 'CAPTCHAの答え',
  'user.signup.form.submitting': '作成中…',
  'user.signup.form.submit': 'アカウントを作成',
  'user.signup.username.available': 'このユーザー名は利用できます。',
  'user.signup.username.taken': 'このユーザー名は既に使用されています。',
  'user.signup.username.invalidChars': '英数字のみ（スペースなし）。',
  'user.signup.username.invalidMin': 'ユーザー名は{{min}}文字以上にしてください。',
  'user.signup.username.invalidGeneric': 'このユーザー名は使用できません。',
  'user.signup.recovery.title': 'リカバリーフレーズを保存',
  'user.signup.recovery.body':
    '表示できるのは今回のみです。書き留めてオフラインで保管してください。パスワードを忘れたときの再設定に必要です。',
  'user.signup.recovery.warn':
    'このフレーズを共有しないでください。知っている人はパスワードを再設定できます。',
  'user.signup.recovery.copy': 'フレーズをコピー',
  'user.signup.recovery.copiedToast': 'コピーしました',
  'user.signup.recovery.checkbox': '12語のリカバリーフレーズを書き留めたか安全に保存しました。',
  'user.signup.recovery.continue': 'ログインへ進む',
};

const forgotJa = forgotExtras({
  needAccountSignup: 'アカウントをお持ちでないですか？ 登録',
  recoveryPhraseLong: '12語のリカバリーフレーズ',
  updating: '更新中…',
  errUser: 'ユーザー名は必須です。',
  errEmail: 'メールアドレスは必須です。',
  errRecovery: 'リカバリーフレーズは必須です。',
  errNewPw: '新しいパスワードは必須です。',
  errNewPwMin: '新しいパスワードは8文字以上にしてください。',
  errMismatch: '新しいパスワードと確認が一致しません。',
  errFail: 'リカバリーに失敗しました',
  successPw: 'パスワードを更新しました — ログインできます',
});

const socialJa = social(
  '出金',
  '{{network}}経由で{{amount}}を出金しました',
  'たった今',
  '30秒前',
  '1分前',
  '2分前',
  '3分前'
);

const signupKo = {
  'user.signup.title': '사용자 가입',
  'user.signup.subtitle': '사용자 패널 계정을 만듭니다.',
  'user.signup.secondaryText': '이미 계정이 있나요?',
  'user.signup.secondaryLabel': '로그인',
  'user.signup.toastCreated': '계정이 생성되었습니다 — 로그인할 수 있습니다.',
  'user.signup.toastSaveRecoveryFirst':
    '계정이 생성되었습니다 — 계속하기 전에 복구 구문을 저장하세요',
  'user.signup.error.nameRequired': '전체 이름은 필수입니다.',
  'user.signup.error.usernameAlphanumeric': '사용자 이름은 영문과 숫자만 사용할 수 있습니다.',
  'user.signup.error.usernameMin': '사용자 이름은 최소 {{min}}자여야 합니다.',
  'user.signup.error.usernameMax': '사용자 이름은 최대 {{max}}자까지입니다.',
  'user.signup.error.usernameTaken': '이 사용자 이름은 이미 사용 중입니다.',
  'user.signup.error.usernameCheckWait': '사용자 이름 확인이 끝날 때까지 기다려 주세요.',
  'user.signup.error.emailRequired': '이메일은 필수입니다.',
  'user.signup.error.passwordRequired': '비밀번호는 필수입니다.',
  'user.signup.error.confirmRequired': '비밀번호 확인은 필수입니다.',
  'user.signup.error.passwordsMismatch': '비밀번호가 일치하지 않습니다.',
  'user.signup.error.passwordMin': '비밀번호는 최소 8자여야 합니다.',
  'user.signup.error.captchaWrong': '캡차는 필수이며 정확해야 합니다.',
  'user.signup.error.referralRequired': '추천 ID는 필수입니다.',
  'user.signup.error.referralInvalid': '추천 ID는 영문과 숫자만 사용할 수 있습니다.',
  'user.signup.error.registrationFailed': '가입에 실패했습니다',
  'user.signup.form.labelFullName': '전체 이름',
  'user.signup.form.placeholderFullName': '전체 이름',
  'user.signup.form.labelUsername': '사용자 이름',
  'user.signup.form.placeholderUsername': '사용자 이름 만들기',
  'user.signup.form.usernameHint': '{{min}}–{{max}}자, 영문과 숫자만.',
  'user.signup.form.labelEmail': '이메일',
  'user.signup.form.placeholderEmail': '이메일 입력',
  'user.signup.form.labelPhone': '전화번호(선택)',
  'user.signup.form.placeholderPhone': '+1… 또는 숫자만',
  'user.signup.form.labelReferral': '추천 ID(필수)',
  'user.signup.form.placeholderReferral': '추천인 사용자 이름',
  'user.signup.form.referralHint':
    '사용자 또는 에이전트 사용자 이름을 입력하세요. 사용자는 사용자를, 에이전트는 사용자를 추천할 수 있습니다.',
  'user.signup.form.labelPassword': '비밀번호(필수)',
  'user.signup.form.placeholderPassword': '비밀번호 만들기',
  'user.signup.form.labelConfirmPassword': '비밀번호 확인(필수)',
  'user.signup.form.placeholderConfirmPassword': '비밀번호 확인',
  'user.signup.form.captchaRobot': '로봇이 아닙니다(필수)',
  'user.signup.form.captchaQuestion': '캡차: {{a}} + {{b}} = ?',
  'user.signup.form.placeholderCaptcha': '캡차 답 입력',
  'user.signup.form.submitting': '만드는 중…',
  'user.signup.form.submit': '계정 만들기',
  'user.signup.username.available': '사용 가능한 사용자 이름입니다.',
  'user.signup.username.taken': '이미 사용 중인 사용자 이름입니다.',
  'user.signup.username.invalidChars': '공백 없이 영문과 숫자만 사용하세요.',
  'user.signup.username.invalidMin': '사용자 이름은 최소 {{min}}자여야 합니다.',
  'user.signup.username.invalidGeneric': '이 사용자 이름은 사용할 수 없습니다.',
  'user.signup.recovery.title': '복구 구문 저장',
  'user.signup.recovery.body':
    '이번에만 보여 드릴 수 있습니다. 적어 두고 오프라인에 보관하세요. 비밀번호를 잊었을 때 재설정에 필요합니다.',
  'user.signup.recovery.warn':
    '이 구문을 공유하지 마세요. 아는 사람은 비밀번호를 재설정할 수 있습니다.',
  'user.signup.recovery.copy': '구문 복사',
  'user.signup.recovery.copiedToast': '구문이 복사되었습니다',
  'user.signup.recovery.checkbox': '12단어 복구 구문을 적었거나 안전하게 저장했습니다.',
  'user.signup.recovery.continue': '로그인으로 계속',
};

const forgotKo = forgotExtras({
  needAccountSignup: '계정이 없나요? 가입하기',
  recoveryPhraseLong: '12단어 복구 구문',
  updating: '업데이트 중…',
  errUser: '사용자 이름은 필수입니다.',
  errEmail: '이메일은 필수입니다.',
  errRecovery: '복구 구문은 필수입니다.',
  errNewPw: '새 비밀번호는 필수입니다.',
  errNewPwMin: '새 비밀번호는 최소 8자여야 합니다.',
  errMismatch: '새 비밀번호와 확인이 일치하지 않습니다.',
  errFail: '복구에 실패했습니다',
  successPw: '비밀번호가 업데이트되었습니다 — 로그인할 수 있습니다',
});

const socialKo = social(
  '출금',
  '{{network}}(으)로 {{amount}} 출금 완료',
  '방금',
  '30초 전',
  '1분 전',
  '2분 전',
  '3분 전'
);

const signupZhCN = {
  'user.signup.title': '用户注册',
  'user.signup.subtitle': '创建您的用户面板账户。',
  'user.signup.secondaryText': '已有账户？',
  'user.signup.secondaryLabel': '登录',
  'user.signup.toastCreated': '账户已创建 — 您现在可以登录。',
  'user.signup.toastSaveRecoveryFirst': '账户已创建 — 请先保存恢复短语再继续',
  'user.signup.error.nameRequired': '姓名为必填项。',
  'user.signup.error.usernameAlphanumeric': '用户名只能包含字母和数字。',
  'user.signup.error.usernameMin': '用户名至少需要 {{min}} 个字符。',
  'user.signup.error.usernameMax': '用户名最多 {{max}} 个字符。',
  'user.signup.error.usernameTaken': '该用户名已被占用。',
  'user.signup.error.usernameCheckWait': '请等待用户名检查完成。',
  'user.signup.error.emailRequired': '邮箱为必填项。',
  'user.signup.error.passwordRequired': '密码为必填项。',
  'user.signup.error.confirmRequired': '确认密码为必填项。',
  'user.signup.error.passwordsMismatch': '两次密码不一致。',
  'user.signup.error.passwordMin': '密码至少需要 8 个字符。',
  'user.signup.error.captchaWrong': '验证码为必填且必须正确。',
  'user.signup.error.referralRequired': '推荐 ID 为必填项。',
  'user.signup.error.referralInvalid': '推荐 ID 只能包含字母和数字。',
  'user.signup.error.registrationFailed': '注册失败',
  'user.signup.form.labelFullName': '全名',
  'user.signup.form.placeholderFullName': '您的全名',
  'user.signup.form.labelUsername': '用户名',
  'user.signup.form.placeholderUsername': '创建用户名',
  'user.signup.form.usernameHint': '{{min}}–{{max}} 个字符，仅限字母和数字。',
  'user.signup.form.labelEmail': '邮箱',
  'user.signup.form.placeholderEmail': '输入邮箱',
  'user.signup.form.labelPhone': '手机号（可选）',
  'user.signup.form.placeholderPhone': '+1… 或仅数字',
  'user.signup.form.labelReferral': '推荐 ID（必填）',
  'user.signup.form.placeholderReferral': '推荐人用户名',
  'user.signup.form.referralHint':
    '输入用户或代理用户名。用户可推荐用户；代理可推荐用户。',
  'user.signup.form.labelPassword': '密码（必填）',
  'user.signup.form.placeholderPassword': '创建密码',
  'user.signup.form.labelConfirmPassword': '确认密码（必填）',
  'user.signup.form.placeholderConfirmPassword': '确认密码',
  'user.signup.form.captchaRobot': '我不是机器人（必填）',
  'user.signup.form.captchaQuestion': '验证码：{{a}} + {{b}} = ？',
  'user.signup.form.placeholderCaptcha': '输入验证码答案',
  'user.signup.form.submitting': '创建中…',
  'user.signup.form.submit': '创建账户',
  'user.signup.username.available': '该用户名可用。',
  'user.signup.username.taken': '该用户名已被占用。',
  'user.signup.username.invalidChars': '仅使用字母和数字（无空格）。',
  'user.signup.username.invalidMin': '用户名至少需要 {{min}} 个字符。',
  'user.signup.username.invalidGeneric': '无法使用该用户名。',
  'user.signup.recovery.title': '保存恢复短语',
  'user.signup.recovery.body':
    '我们只能在此时显示一次。请写下并离线保存。忘记密码时需要用它重置。',
  'user.signup.recovery.warn': '切勿分享此短语。拥有它的人可以重置您的密码。',
  'user.signup.recovery.copy': '复制短语',
  'user.signup.recovery.copiedToast': '已复制短语',
  'user.signup.recovery.checkbox': '我已写下或安全保存了 12 词恢复短语。',
  'user.signup.recovery.continue': '继续登录',
};

const forgotZhCN = forgotExtras({
  needAccountSignup: '没有账户？注册',
  recoveryPhraseLong: '12 词恢复短语',
  updating: '更新中…',
  errUser: '用户名为必填项。',
  errEmail: '邮箱为必填项。',
  errRecovery: '恢复短语为必填项。',
  errNewPw: '新密码为必填项。',
  errNewPwMin: '新密码至少需要 8 个字符。',
  errMismatch: '新密码与确认不一致。',
  errFail: '恢复失败',
  successPw: '密码已更新 — 您可以登录',
});

const socialZhCN = social(
  '提现',
  '已通过 {{network}} 成功提现 {{amount}}',
  '刚刚',
  '30 秒前',
  '1 分钟前',
  '2 分钟前',
  '3 分钟前'
);

const signupPt = {
  'user.signup.title': 'Cadastro de usuário',
  'user.signup.subtitle': 'Crie sua conta do painel do usuário.',
  'user.signup.secondaryText': 'Já tem uma conta?',
  'user.signup.secondaryLabel': 'Entrar',
  'user.signup.toastCreated': 'Conta criada — você já pode entrar.',
  'user.signup.toastSaveRecoveryFirst':
    'Conta criada — salve sua frase de recuperação antes de continuar',
  'user.signup.error.nameRequired': 'O nome completo é obrigatório.',
  'user.signup.error.usernameAlphanumeric': 'O nome de usuário deve conter apenas letras e números.',
  'user.signup.error.usernameMin': 'O nome de usuário deve ter pelo menos {{min}} caracteres.',
  'user.signup.error.usernameMax': 'O nome de usuário deve ter no máximo {{max}} caracteres.',
  'user.signup.error.usernameTaken': 'Este nome de usuário já está em uso.',
  'user.signup.error.usernameCheckWait': 'Aguarde a verificação do nome de usuário terminar.',
  'user.signup.error.emailRequired': 'O e-mail é obrigatório.',
  'user.signup.error.passwordRequired': 'A senha é obrigatória.',
  'user.signup.error.confirmRequired': 'A confirmação da senha é obrigatória.',
  'user.signup.error.passwordsMismatch': 'As senhas não coincidem.',
  'user.signup.error.passwordMin': 'A senha deve ter pelo menos 8 caracteres.',
  'user.signup.error.captchaWrong': 'O captcha é obrigatório e deve estar correto.',
  'user.signup.error.referralRequired': 'O ID de indicação é obrigatório.',
  'user.signup.error.referralInvalid': 'O ID de indicação só pode conter letras e números.',
  'user.signup.error.registrationFailed': 'Falha no cadastro',
  'user.signup.form.labelFullName': 'Nome completo',
  'user.signup.form.placeholderFullName': 'Seu nome completo',
  'user.signup.form.labelUsername': 'Nome de usuário',
  'user.signup.form.placeholderUsername': 'Criar nome de usuário',
  'user.signup.form.usernameHint': '{{min}}–{{max}} caracteres, apenas letras e números.',
  'user.signup.form.labelEmail': 'E-mail',
  'user.signup.form.placeholderEmail': 'Digite o e-mail',
  'user.signup.form.labelPhone': 'Telefone (opcional)',
  'user.signup.form.placeholderPhone': '+1… ou apenas dígitos',
  'user.signup.form.labelReferral': 'ID de indicação (obrigatório)',
  'user.signup.form.placeholderReferral': 'Nome de usuário do indicador',
  'user.signup.form.referralHint':
    'Informe um usuário ou agente. Usuários indicam usuários; agentes podem indicar usuários.',
  'user.signup.form.labelPassword': 'Senha (obrigatória)',
  'user.signup.form.placeholderPassword': 'Criar senha',
  'user.signup.form.labelConfirmPassword': 'Confirmar senha (obrigatória)',
  'user.signup.form.placeholderConfirmPassword': 'Confirmar senha',
  'user.signup.form.captchaRobot': 'Não sou um robô (obrigatório)',
  'user.signup.form.captchaQuestion': 'Captcha: quanto é {{a}} + {{b}}?',
  'user.signup.form.placeholderCaptcha': 'Resposta do captcha',
  'user.signup.form.submitting': 'Criando…',
  'user.signup.form.submit': 'Criar conta',
  'user.signup.username.available': 'Este nome de usuário está disponível.',
  'user.signup.username.taken': 'Este nome de usuário já está em uso.',
  'user.signup.username.invalidChars': 'Use apenas letras e números (sem espaços).',
  'user.signup.username.invalidMin': 'O nome de usuário deve ter pelo menos {{min}} caracteres.',
  'user.signup.username.invalidGeneric': 'Este nome de usuário não pode ser usado.',
  'user.signup.recovery.title': 'Guarde sua frase de recuperação',
  'user.signup.recovery.body':
    'Só podemos mostrá-la desta vez. Anote e guarde offline. Você precisará dela para redefinir a senha se esquecer.',
  'user.signup.recovery.warn':
    'Nunca compartilhe esta frase. Quem a tiver pode redefinir sua senha.',
  'user.signup.recovery.copy': 'Copiar frase',
  'user.signup.recovery.copiedToast': 'Frase copiada',
  'user.signup.recovery.checkbox':
    'Anotei ou salvei com segurança minha frase de recuperação de 12 palavras.',
  'user.signup.recovery.continue': 'Continuar para entrar',
};

const forgotPt = forgotExtras({
  needAccountSignup: 'Precisa de conta? Cadastre-se',
  recoveryPhraseLong: 'Frase de recuperação de 12 palavras',
  updating: 'Atualizando…',
  errUser: 'O nome de usuário é obrigatório.',
  errEmail: 'O e-mail é obrigatório.',
  errRecovery: 'A frase de recuperação é obrigatória.',
  errNewPw: 'A nova senha é obrigatória.',
  errNewPwMin: 'A nova senha deve ter pelo menos 8 caracteres.',
  errMismatch: 'A nova senha e a confirmação não coincidem.',
  errFail: 'Falha na recuperação',
  successPw: 'Senha atualizada — você pode entrar',
});

const socialPt = social(
  'Saque',
  'Sacou com sucesso {{amount}} via {{network}}',
  'agora mesmo',
  'há 30 s',
  'há 1 min',
  'há 2 min',
  'há 3 min'
);

const signupIt = {
  'user.signup.title': 'Registrazione utente',
  'user.signup.subtitle': 'Crea il tuo account del pannello utente.',
  'user.signup.secondaryText': 'Hai già un account?',
  'user.signup.secondaryLabel': 'Accedi',
  'user.signup.toastCreated': 'Account creato — puoi accedere.',
  'user.signup.toastSaveRecoveryFirst':
    'Account creato — salva la frase di recuperazione prima di continuare',
  'user.signup.error.nameRequired': 'Il nome completo è obbligatorio.',
  'user.signup.error.usernameAlphanumeric': 'Il nome utente deve contenere solo lettere e numeri.',
  'user.signup.error.usernameMin': 'Il nome utente deve avere almeno {{min}} caratteri.',
  'user.signup.error.usernameMax': 'Il nome utente deve avere al massimo {{max}} caratteri.',
  'user.signup.error.usernameTaken': 'Questo nome utente è già in uso.',
  'user.signup.error.usernameCheckWait': 'Attendi il completamento del controllo del nome utente.',
  'user.signup.error.emailRequired': "L'email è obbligatoria.",
  'user.signup.error.passwordRequired': 'La password è obbligatoria.',
  'user.signup.error.confirmRequired': 'La conferma della password è obbligatoria.',
  'user.signup.error.passwordsMismatch': 'Le password non coincidono.',
  'user.signup.error.passwordMin': 'La password deve avere almeno 8 caratteri.',
  'user.signup.error.captchaWrong': 'Il captcha è obbligatorio e deve essere corretto.',
  'user.signup.error.referralRequired': 'ID referral obbligatorio.',
  'user.signup.error.referralInvalid': 'L\'ID referral può contenere solo lettere e numeri.',
  'user.signup.error.registrationFailed': 'Registrazione non riuscita',
  'user.signup.form.labelFullName': 'Nome completo',
  'user.signup.form.placeholderFullName': 'Il tuo nome completo',
  'user.signup.form.labelUsername': 'Nome utente',
  'user.signup.form.placeholderUsername': 'Crea nome utente',
  'user.signup.form.usernameHint': '{{min}}–{{max}} caratteri, solo lettere e numeri.',
  'user.signup.form.labelEmail': 'Email',
  'user.signup.form.placeholderEmail': 'Inserisci email',
  'user.signup.form.labelPhone': 'Numero di telefono (opzionale)',
  'user.signup.form.placeholderPhone': '+1… o solo cifre',
  'user.signup.form.labelReferral': 'ID referral (obbligatorio)',
  'user.signup.form.placeholderReferral': 'Nome utente del referente',
  'user.signup.form.referralHint':
    'Inserisci un utente o un agente. Gli utenti possono invitare utenti; gli agenti possono invitare utenti.',
  'user.signup.form.labelPassword': 'Password (obbligatoria)',
  'user.signup.form.placeholderPassword': 'Crea password',
  'user.signup.form.labelConfirmPassword': 'Conferma password (obbligatoria)',
  'user.signup.form.placeholderConfirmPassword': 'Conferma password',
  'user.signup.form.captchaRobot': 'Non sono un robot (obbligatorio)',
  'user.signup.form.captchaQuestion': 'Captcha: quanto fa {{a}} + {{b}}?',
  'user.signup.form.placeholderCaptcha': 'Risposta captcha',
  'user.signup.form.submitting': 'Creazione…',
  'user.signup.form.submit': 'Crea account',
  'user.signup.username.available': 'Questo nome utente è disponibile.',
  'user.signup.username.taken': 'Questo nome utente è già in uso.',
  'user.signup.username.invalidChars': 'Usa solo lettere e numeri (senza spazi).',
  'user.signup.username.invalidMin': 'Il nome utente deve avere almeno {{min}} caratteri.',
  'user.signup.username.invalidGeneric': 'Questo nome utente non può essere usato.',
  'user.signup.recovery.title': 'Salva la frase di recupero',
  'user.signup.recovery.body':
    'Possiamo mostrarla solo questa volta. Annotala e conservala offline. Ti servirà per reimpostare la password se la dimentichi.',
  'user.signup.recovery.warn':
    'Non condividere mai questa frase. Chi la possiede può reimpostare la password.',
  'user.signup.recovery.copy': 'Copia frase',
  'user.signup.recovery.copiedToast': 'Frase copiata',
  'user.signup.recovery.checkbox':
    'Ho annotato o salvato in modo sicuro la mia frase di recupero da 12 parole.',
  'user.signup.recovery.continue': 'Continua per accedere',
};

const forgotIt = forgotExtras({
  needAccountSignup: 'Serve un account? Registrati',
  recoveryPhraseLong: 'Frase di recupero da 12 parole',
  updating: 'Aggiornamento…',
  errUser: 'Il nome utente è obbligatorio.',
  errEmail: "L'email è obbligatoria.",
  errRecovery: 'La frase di recupero è obbligatoria.',
  errNewPw: 'La nuova password è obbligatoria.',
  errNewPwMin: 'La nuova password deve avere almeno 8 caratteri.',
  errMismatch: 'La nuova password e la conferma non coincidono.',
  errFail: 'Recupero non riuscito',
  successPw: 'Password aggiornata — puoi accedere',
});

const socialIt = social(
  'Prelievo',
  'Prelevato con successo {{amount}} tramite {{network}}',
  'proprio ora',
  '30 s fa',
  '1 min fa',
  '2 min fa',
  '3 min fa'
);

const signupRu = {
  'user.signup.title': 'Регистрация пользователя',
  'user.signup.subtitle': 'Создайте аккаунт пользовательской панели.',
  'user.signup.secondaryText': 'Уже есть аккаунт?',
  'user.signup.secondaryLabel': 'Войти',
  'user.signup.toastCreated': 'Аккаунт создан — можно войти.',
  'user.signup.toastSaveRecoveryFirst':
    'Аккаунт создан — сохраните фразу восстановления перед продолжением',
  'user.signup.error.nameRequired': 'Укажите полное имя.',
  'user.signup.error.usernameAlphanumeric': 'Имя пользователя: только буквы и цифры.',
  'user.signup.error.usernameMin': 'Имя пользователя — не менее {{min}} символов.',
  'user.signup.error.usernameMax': 'Имя пользователя — не более {{max}} символов.',
  'user.signup.error.usernameTaken': 'Это имя пользователя уже занято.',
  'user.signup.error.usernameCheckWait': 'Дождитесь проверки имени пользователя.',
  'user.signup.error.emailRequired': 'Укажите email.',
  'user.signup.error.passwordRequired': 'Пароль обязателен.',
  'user.signup.error.confirmRequired': 'Подтверждение пароля обязательно.',
  'user.signup.error.passwordsMismatch': 'Пароли не совпадают.',
  'user.signup.error.passwordMin': 'Пароль должен быть не короче 8 символов.',
  'user.signup.error.captchaWrong': 'Капча обязательна и должна быть верной.',
  'user.signup.error.referralRequired': 'Реферальный ID обязателен.',
  'user.signup.error.referralInvalid': 'Реферальный ID может содержать только буквы и цифры.',
  'user.signup.error.registrationFailed': 'Регистрация не удалась',
  'user.signup.form.labelFullName': 'Полное имя',
  'user.signup.form.placeholderFullName': 'Ваше полное имя',
  'user.signup.form.labelUsername': 'Имя пользователя',
  'user.signup.form.placeholderUsername': 'Придумайте имя пользователя',
  'user.signup.form.usernameHint': '{{min}}–{{max}} символов, только буквы и цифры.',
  'user.signup.form.labelEmail': 'Email',
  'user.signup.form.placeholderEmail': 'Введите email',
  'user.signup.form.labelPhone': 'Телефон (необязательно)',
  'user.signup.form.placeholderPhone': '+1… или только цифры',
  'user.signup.form.labelReferral': 'Реферальный ID (обязательно)',
  'user.signup.form.placeholderReferral': 'Имя пригласившего',
  'user.signup.form.referralHint':
    'Укажите пользователя или агента. Пользователи приглашают пользователей; агенты могут приглашать пользователей.',
  'user.signup.form.labelPassword': 'Пароль (обязательно)',
  'user.signup.form.placeholderPassword': 'Придумайте пароль',
  'user.signup.form.labelConfirmPassword': 'Подтверждение пароля (обязательно)',
  'user.signup.form.placeholderConfirmPassword': 'Подтвердите пароль',
  'user.signup.form.captchaRobot': 'Я не робот (обязательно)',
  'user.signup.form.captchaQuestion': 'Капча: сколько {{a}} + {{b}}?',
  'user.signup.form.placeholderCaptcha': 'Ответ на капчу',
  'user.signup.form.submitting': 'Создание…',
  'user.signup.form.submit': 'Создать аккаунт',
  'user.signup.username.available': 'Имя пользователя свободно.',
  'user.signup.username.taken': 'Имя пользователя уже занято.',
  'user.signup.username.invalidChars': 'Только буквы и цифры (без пробелов).',
  'user.signup.username.invalidMin': 'Имя пользователя — не менее {{min}} символов.',
  'user.signup.username.invalidGeneric': 'Это имя пользователя нельзя использовать.',
  'user.signup.recovery.title': 'Сохраните фразу восстановления',
  'user.signup.recovery.body':
    'Мы показываем её только сейчас. Запишите и храните офлайн. Она нужна для сброса пароля.',
  'user.signup.recovery.warn':
    'Никому не сообщайте эту фразу. У кого она есть — может сбросить пароль.',
  'user.signup.recovery.copy': 'Скопировать фразу',
  'user.signup.recovery.copiedToast': 'Фраза скопирована',
  'user.signup.recovery.checkbox':
    'Я записал или надёжно сохранил 12-словную фразу восстановления.',
  'user.signup.recovery.continue': 'Перейти ко входу',
};

const forgotRu = forgotExtras({
  needAccountSignup: 'Нет аккаунта? Зарегистрироваться',
  recoveryPhraseLong: '12-словная фраза восстановления',
  updating: 'Обновление…',
  errUser: 'Укажите имя пользователя.',
  errEmail: 'Укажите email.',
  errRecovery: 'Укажите фразу восстановления.',
  errNewPw: 'Укажите новый пароль.',
  errNewPwMin: 'Новый пароль — не менее 8 символов.',
  errMismatch: 'Новый пароль и подтверждение не совпадают.',
  errFail: 'Восстановление не удалось',
  successPw: 'Пароль обновлён — можно войти',
});

const socialRu = social(
  'Вывод',
  'Успешно выведено {{amount}} через {{network}}',
  'только что',
  '30 с назад',
  '1 мин назад',
  '2 мин назад',
  '3 мин назад'
);

const signupBn = {
  'user.signup.title': 'ব্যবহারকারী নিবন্ধন',
  'user.signup.subtitle': 'আপনার ব্যবহারকারী প্যানেল অ্যাকাউন্ট তৈরি করুন।',
  'user.signup.secondaryText': 'ইতিমধ্যে অ্যাকাউন্ট আছে?',
  'user.signup.secondaryLabel': 'সাইন ইন',
  'user.signup.toastCreated': 'অ্যাকাউন্ট তৈরি হয়েছে — এখন সাইন ইন করতে পারেন।',
  'user.signup.toastSaveRecoveryFirst':
    'অ্যাকাউন্ট তৈরি হয়েছে — চালিয়ে যাওয়ার আগে আপনার পুনরুদ্ধার বাক্যাংশ সংরক্ষণ করুন',
  'user.signup.error.nameRequired': 'পূর্ণ নাম প্রয়োজন।',
  'user.signup.error.usernameAlphanumeric': 'ব্যবহারকারীর নামে শুধু অক্ষর ও সংখ্যা থাকতে হবে।',
  'user.signup.error.usernameMin': 'ব্যবহারকারীর নাম কমপক্ষে {{min}} অক্ষরের হতে হবে।',
  'user.signup.error.usernameMax': 'ব্যবহারকারীর নাম সর্বোচ্চ {{max}} অক্ষরের হতে হবে।',
  'user.signup.error.usernameTaken': 'এই ব্যবহারকারীর নামটি ইতিমধ্যে নেওয়া।',
  'user.signup.error.usernameCheckWait': 'ব্যবহারকারীর নাম পরীক্ষা শেষ হওয়া পর্যন্ত অপেক্ষা করুন।',
  'user.signup.error.emailRequired': 'ইমেইল প্রয়োজন।',
  'user.signup.error.passwordRequired': 'পাসওয়ার্ড বাধ্যতামূলক।',
  'user.signup.error.confirmRequired': 'পাসওয়ার্ড নিশ্চিতকরণ বাধ্যতামূলক।',
  'user.signup.error.passwordsMismatch': 'পাসওয়ার্ড মিলছে না।',
  'user.signup.error.passwordMin': 'পাসওয়ার্ড কমপক্ষে ৮ অক্ষরের হতে হবে।',
  'user.signup.error.captchaWrong': 'ক্যাপচা বাধ্যতামূলক এবং সঠিক হতে হবে।',
  'user.signup.error.referralRequired': 'রেফারেল আইডি বাধ্যতামূলক।',
  'user.signup.error.referralInvalid': 'রেফারেল আইডিতে শুধু অক্ষর ও সংখ্যা থাকতে হবে।',
  'user.signup.error.registrationFailed': 'নিবন্ধন ব্যর্থ হয়েছে',
  'user.signup.form.labelFullName': 'পূর্ণ নাম',
  'user.signup.form.placeholderFullName': 'আপনার পূর্ণ নাম',
  'user.signup.form.labelUsername': 'ব্যবহারকারীর নাম',
  'user.signup.form.placeholderUsername': 'ব্যবহারকারীর নাম তৈরি করুন',
  'user.signup.form.usernameHint': '{{min}}–{{max}} অক্ষর, শুধু অক্ষর ও সংখ্যা।',
  'user.signup.form.labelEmail': 'ইমেইল',
  'user.signup.form.placeholderEmail': 'ইমেইল লিখুন',
  'user.signup.form.labelPhone': 'ফোন নম্বর (ঐচ্ছিক)',
  'user.signup.form.placeholderPhone': '+1… অথবা শুধু সংখ্যা',
  'user.signup.form.labelReferral': 'রেফারেল আইডি (বাধ্যতামূলক)',
  'user.signup.form.placeholderReferral': 'রেফারারের ব্যবহারকারীর নাম',
  'user.signup.form.referralHint':
    'ব্যবহারকারী বা এজেন্টের ব্যবহারকারীর নাম দিন। ব্যবহারকারীরা ব্যবহারকারীকে, এজেন্টরা ব্যবহারকারীকে রেফার করতে পারে।',
  'user.signup.form.labelPassword': 'পাসওয়ার্ড (বাধ্যতামূলক)',
  'user.signup.form.placeholderPassword': 'পাসওয়ার্ড তৈরি করুন',
  'user.signup.form.labelConfirmPassword': 'পাসওয়ার্ড নিশ্চিত করুন (বাধ্যতামূলক)',
  'user.signup.form.placeholderConfirmPassword': 'পাসওয়ার্ড নিশ্চিত করুন',
  'user.signup.form.captchaRobot': 'আমি রোবট নই (বাধ্যতামূলক)',
  'user.signup.form.captchaQuestion': 'ক্যাপচা: {{a}} + {{b}} কত?',
  'user.signup.form.placeholderCaptcha': 'ক্যাপচার উত্তর লিখুন',
  'user.signup.form.submitting': 'তৈরি হচ্ছে…',
  'user.signup.form.submit': 'অ্যাকাউন্ট তৈরি করুন',
  'user.signup.username.available': 'এই ব্যবহারকারীর নামটি উপলব্ধ।',
  'user.signup.username.taken': 'এই ব্যবহারকারীর নামটি ইতিমধ্যে নেওয়া।',
  'user.signup.username.invalidChars': 'শুধু অক্ষর ও সংখ্যা ব্যবহার করুন (ফাঁকা নয়)।',
  'user.signup.username.invalidMin': 'ব্যবহারকারীর নাম কমপক্ষে {{min}} অক্ষরের হতে হবে।',
  'user.signup.username.invalidGeneric': 'এই ব্যবহারকারীর নাম ব্যবহার করা যাবে না।',
  'user.signup.recovery.title': 'আপনার পুনরুদ্ধার বাক্যাংশ সংরক্ষণ করুন',
  'user.signup.recovery.body':
    'আমরা এটি শুধু এইবার দেখাতে পারি। লিখে অফলাইনে রাখুন। পাসওয়ার্ড ভুলে গেলে এটি লাগবে।',
  'user.signup.recovery.warn':
    'এই বাক্যাংশ কখনও শেয়ার করবেন না। যার কাছে আছে সে পাসওয়ার্ড রিসেট করতে পারে।',
  'user.signup.recovery.copy': 'বাক্যাংশ কপি করুন',
  'user.signup.recovery.copiedToast': 'বাক্যাংশ কপি হয়েছে',
  'user.signup.recovery.checkbox':
    'আমি আমার ১২-শব্দের পুনরুদ্ধার বাক্যাংশ লিখেছি বা নিরাপদে সংরক্ষণ করেছি।',
  'user.signup.recovery.continue': 'সাইন ইনে চালিয়ে যান',
};

const forgotBn = forgotExtras({
  needAccountSignup: 'অ্যাকাউন্ট লাগবে? সাইন আপ',
  recoveryPhraseLong: '১২-শব্দের পুনরুদ্ধার বাক্যাংশ',
  updating: 'আপডেট হচ্ছে…',
  errUser: 'ব্যবহারকারীর নাম প্রয়োজন।',
  errEmail: 'ইমেইল প্রয়োজন।',
  errRecovery: 'পুনরুদ্ধার বাক্যাংশ প্রয়োজন।',
  errNewPw: 'নতুন পাসওয়ার্ড প্রয়োজন।',
  errNewPwMin: 'নতুন পাসওয়ার্ড কমপক্ষে ৮ অক্ষরের হতে হবে।',
  errMismatch: 'নতুন পাসওয়ার্ড ও নিশ্চিতকরণ মিলছে না।',
  errFail: 'পুনরুদ্ধার ব্যর্থ',
  successPw: 'পাসওয়ার্ড আপডেট হয়েছে — আপনি সাইন ইন করতে পারেন',
});

const socialBn = social(
  'উত্তোলন',
  '{{network}} এর মাধ্যমে সফলভাবে {{amount}} উত্তোলন',
  'এইমাত্র',
  '৩০ সেক আগে',
  '১ মিন আগে',
  '২ মিন আগে',
  '৩ মিন আগে'
);

const signupHi = {
  'user.signup.title': 'उपयोगकर्ता पंजीकरण',
  'user.signup.subtitle': 'अपना उपयोगकर्ता पैनल खाता बनाएं।',
  'user.signup.secondaryText': 'पहले से खाता है?',
  'user.signup.secondaryLabel': 'साइन इन',
  'user.signup.toastCreated': 'खाता बन गया — अब आप साइन इन कर सकते हैं।',
  'user.signup.toastSaveRecoveryFirst':
    'खाता बन गया — जारी रखने से पहले अपना रिकवरी वाक्यांश सहेजें',
  'user.signup.error.nameRequired': 'पूरा नाम आवश्यक है।',
  'user.signup.error.usernameAlphanumeric': 'उपयोगकर्ता नाम में केवल अक्षर और अंक हों।',
  'user.signup.error.usernameMin': 'उपयोगकर्ता नाम कम से कम {{min}} अक्षर का हो।',
  'user.signup.error.usernameMax': 'उपयोगकर्ता नाम अधिकतम {{max}} अक्षर का हो।',
  'user.signup.error.usernameTaken': 'यह उपयोगकर्ता नाम पहले से लिया गया है।',
  'user.signup.error.usernameCheckWait': 'उपयोगकर्ता नाम जांच पूरी होने तक प्रतीक्षा करें।',
  'user.signup.error.emailRequired': 'ईमेल आवश्यक है।',
  'user.signup.error.passwordRequired': 'पासवर्ड अनिवार्य है।',
  'user.signup.error.confirmRequired': 'पासवर्ड पुष्टि अनिवार्य है।',
  'user.signup.error.passwordsMismatch': 'पासवर्ड मेल नहीं खाते।',
  'user.signup.error.passwordMin': 'पासवर्ड कम से कम 8 अक्षर का हो।',
  'user.signup.error.captchaWrong': 'कैप्चा अनिवार्य है और सही होना चाहिए।',
  'user.signup.error.referralRequired': 'रेफ़रल ID अनिवार्य है।',
  'user.signup.error.referralInvalid': 'रेफ़रल ID में केवल अक्षर और संख्याएँ हो सकती हैं।',
  'user.signup.error.registrationFailed': 'पंजीकरण विफल',
  'user.signup.form.labelFullName': 'पूरा नाम',
  'user.signup.form.placeholderFullName': 'आपका पूरा नाम',
  'user.signup.form.labelUsername': 'उपयोगकर्ता नाम',
  'user.signup.form.placeholderUsername': 'उपयोगकर्ता नाम बनाएं',
  'user.signup.form.usernameHint': '{{min}}–{{max}} अक्षर, केवल अक्षर और अंक।',
  'user.signup.form.labelEmail': 'ईमेल',
  'user.signup.form.placeholderEmail': 'ईमेल दर्ज करें',
  'user.signup.form.labelPhone': 'फ़ोन नंबर (वैकल्पिक)',
  'user.signup.form.placeholderPhone': '+1… या केवल अंक',
  'user.signup.form.labelReferral': 'रेफ़रल ID (अनिवार्य)',
  'user.signup.form.placeholderReferral': 'रेफ़रर उपयोगकर्ता नाम',
  'user.signup.form.referralHint':
    'उपयोगकर्ता या एजेंट का उपयोगकर्ता नाम दर्ज करें। उपयोगकर्ता उपयोगकर्ता को, एजेंट उपयोगकर्ता को रेफर कर सकते हैं।',
  'user.signup.form.labelPassword': 'पासवर्ड (अनिवार्य)',
  'user.signup.form.placeholderPassword': 'पासवर्ड बनाएं',
  'user.signup.form.labelConfirmPassword': 'पासवर्ड की पुष्टि (अनिवार्य)',
  'user.signup.form.placeholderConfirmPassword': 'पासवर्ड की पुष्टि करें',
  'user.signup.form.captchaRobot': 'मैं रोबोट नहीं हूँ (अनिवार्य)',
  'user.signup.form.captchaQuestion': 'कैप्चा: {{a}} + {{b}} कितना है?',
  'user.signup.form.placeholderCaptcha': 'कैप्चा उत्तर दर्ज करें',
  'user.signup.form.submitting': 'बन रहा है…',
  'user.signup.form.submit': 'खाता बनाएं',
  'user.signup.username.available': 'यह उपयोगकर्ता नाम उपलब्ध है।',
  'user.signup.username.taken': 'यह उपयोगकर्ता नाम पहले से लिया गया है।',
  'user.signup.username.invalidChars': 'केवल अक्षर और अंक (बिना रिक्त स्थान)।',
  'user.signup.username.invalidMin': 'उपयोगकर्ता नाम कम से कम {{min}} अक्षर का हो।',
  'user.signup.username.invalidGeneric': 'यह उपयोगकर्ता नाम उपयोग नहीं किया जा सकता।',
  'user.signup.recovery.title': 'अपना रिकवरी वाक्यांश सहेजें',
  'user.signup.recovery.body':
    'हम इसे केवल इस बार दिखा सकते हैं। लिखकर ऑफ़लाइन रखें। पासवर्ड भूलने पर रीसेट के लिए चाहिए।',
  'user.signup.recovery.warn':
    'इस वाक्यांश को कभी साझा न करें। जिसके पास है वह पासवर्ड रीसेट कर सकता है।',
  'user.signup.recovery.copy': 'वाक्यांश कॉपी करें',
  'user.signup.recovery.copiedToast': 'वाक्यांश कॉपी हो गया',
  'user.signup.recovery.checkbox':
    'मैंने अपना 12-शब्द रिकवरी वाक्यांश लिख लिया है या सुरक्षित रूप से सहेजा है।',
  'user.signup.recovery.continue': 'साइन इन पर जारी रखें',
};

const forgotHi = forgotExtras({
  needAccountSignup: 'खाता चाहिए? साइन अप करें',
  recoveryPhraseLong: '12-शब्द रिकवरी वाक्यांश',
  updating: 'अपडेट हो रहा है…',
  errUser: 'उपयोगकर्ता नाम आवश्यक है।',
  errEmail: 'ईमेल आवश्यक है।',
  errRecovery: 'रिकवरी वाक्यांश आवश्यक है।',
  errNewPw: 'नया पासवर्ड आवश्यक है।',
  errNewPwMin: 'नया पासवर्ड कम से कम 8 अक्षर का हो।',
  errMismatch: 'नया पासवर्ड और पुष्टि मेल नहीं खाते।',
  errFail: 'रिकवरी विफल',
  successPw: 'पासवर्ड अपडेट — अब साइन इन कर सकते हैं',
});

const socialHi = social(
  'निकासी',
  '{{network}} के माध्यम से सफलतापूर्वक {{amount}} निकाला',
  'अभी',
  '30 सेक पहले',
  '1 मिन पहले',
  '2 मिन पहले',
  '3 मिन पहले'
);

export const panelUserSignupSocialForgotByLang = {
  es: { ...signupEs, ...forgotEs },
  de: { ...signupDe, ...socialDe, ...forgotDe },
  fr: { ...signupFr, ...socialFr, ...forgotFr },
  ar: { ...signupAr, ...socialAr, ...forgotAr },
  ja: { ...signupJa, ...socialJa, ...forgotJa },
  ko: { ...signupKo, ...socialKo, ...forgotKo },
  'zh-CN': { ...signupZhCN, ...socialZhCN, ...forgotZhCN },
  pt: { ...signupPt, ...socialPt, ...forgotPt },
  it: { ...signupIt, ...socialIt, ...forgotIt },
  ru: { ...signupRu, ...socialRu, ...forgotRu },
  bn: { ...signupBn, ...socialBn, ...forgotBn },
  hi: { ...signupHi, ...socialHi, ...forgotHi },
};
